import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { enablePush, pushSupported, pushPermission } from "../lib/push";

const DISMISS_KEY = "qdn_push_card_dismissed";

export function EnableNotificationsCard() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) return;
    if (!pushSupported()) return;
    if (pushPermission() === "granted") return; // already enabled
    setVisible(true);
  }, [user]);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Notifications enabled! We'll keep you posted.");
      localStorage.setItem(DISMISS_KEY, "1");
      setVisible(false);
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="enable-notifications-card"
      className="relative mb-4 flex items-center gap-3 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-white p-4 shadow-sm"
    >
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#FF5200] text-white">
        <Bell size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-extrabold text-slate-900">Stay updated</p>
        <p className="text-xs text-slate-600">Get order updates, offers & delivery alerts.</p>
      </div>
      <button
        data-testid="enable-notifications-btn"
        onClick={enable}
        disabled={busy}
        className="shrink-0 rounded-full bg-[#FF5200] px-4 py-2 text-xs font-bold text-white hover:bg-orange-600 disabled:opacity-60"
      >
        {busy ? "Enabling…" : "Enable"}
      </button>
      <button
        data-testid="enable-notifications-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 text-slate-400 hover:text-slate-700"
      >
        <X size={16} />
      </button>
    </div>
  );
}
